// =============================================================================
// io/sync/coaching.ts — a four-column slice of the WHOLE book.
// -----------------------------------------------------------------------------
// What the "FOR YOU TODAY" card needs and nothing else: which setup, what it
// made, what went wrong, and whose account it was on.
//
// ── WHY A THIRD TRADES READ, RATHER THAN WIDENING ONE ──
//
// The card's claim is about the trader, not about a window: "your strongest edge
// is X" answered over the last 45 days names a different pattern with different
// figures while reading like an all-time statement. So it needs the whole book.
//
// The two existing reads cannot supply that cheaply:
//
//   loadTradeMetrics    11 columns, but no `mistake_tags` and no `pattern_id`
//   loadTradesInRange   24 columns, no `mistake_tags`, and pulling all of them
//                       across the whole book is the mobile-transfer cost D47
//                       measured (2.00 MB ≈ 1.6 s on 4G) — to print two sentences
//
// Adding `mistake_tags` to `TABLE_KEYS` would put a text[] on every trades-table
// read forever, for a column the table does not display. Four columns over the
// whole book is a fraction of one page per thousand trades.
//
// ── WHY THERE IS NO ReadReceipt HERE ──
//
// `selectors/population.ts` exists because `loadTradesInRange` TAKES bounds, so a
// caller can hand a Class-L surface a short read and get a plausible wrong number.
// This function takes no bounds, applies no filter, pages to exhaustion and throws
// on the first bad page. There is no way to call it and receive less than the
// whole book — the guarantee is structural rather than carried in a receipt, and
// a receipt that can only ever say `coversWholeBook: true` proves nothing.
//
// ⚠ If a `since` parameter is ever added here, that stops being true and this
// read owes its callers a receipt.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { TRADE_COLUMNS, type Trade, type TradeColumnKey } from '../../domain/types';
import { PAGE_ROWS } from './load';
import { PROJECTION_TAINT } from './hydration';

export const COACHING_KEYS = [
  // The account, so the selection filter and the exclude-from-metrics rule can
  // both be applied — a SIM account's trades must not name the trader's edge.
  'accountId',
  'patternId', 'netPnl', 'mistakeTags',
] as const satisfies readonly TradeColumnKey[];

export type CoachingRow = Pick<Trade, (typeof COACHING_KEYS)[number]>;

const COACHING_SELECT = COACHING_KEYS.map((k) => TRADE_COLUMNS[k]).join(',');

export function coachingRowFromDb(r: Record<string, unknown>): CoachingRow {
  const out: Record<string, unknown> = {
    accountId: r['account_id'] ?? null,
    patternId: r['pattern_id'] ?? null,
    // ⚠ `?? null`, not `|| null` — a 0 P&L is a real scratch and the card counts
    // it in the sample.
    netPnl: r['net_pnl'] ?? null,
    mistakeTags: r['mistake_tags'] ?? [],
  };

  // ⚠ TAINTED, for the same reason `tableRowFromDb` is: these rows carry 4 of 48
  // columns, and the marker travels through spread, clone and JSON so no object
  // assembled from one can reach a writer looking complete. Nothing here is
  // editable today — which is exactly when the marker is free to add and exactly
  // when it gets forgotten.
  out[PROJECTION_TAINT] = 'coaching';

  return out as unknown as CoachingRow;
}

/**
 * Every trade the user has, in four columns.
 *
 * ⚠ Throws on the first bad page rather than returning what arrived. A short
 * result here does not look short: it produces a confident, plausible, wrong
 * "your strongest edge" — the optimistic-direction failure D47 is about.
 */
export async function loadCoachingUniverse(sb: SupabaseClient): Promise<CoachingRow[]> {
  const out: CoachingRow[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades')
      .select(COACHING_SELECT)
      // ⚠ ORDERED, which is what makes `range()` paging correct rather than
      // decorative: over an unordered query a concurrent insert shifts rows
      // across a page boundary, dropping one and duplicating another silently.
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    if (error) {
      throw new Error(
        `loadCoachingUniverse: read failed on page ${from / PAGE_ROWS + 1}: ${error.message}. ` +
          `Refusing to return a partial book — "your strongest edge" computed from part of ` +
          `it is a confident claim about the wrong setup.`,
      );
    }
    if (!data || !data.length) break;

    for (const r of data as unknown as Record<string, unknown>[]) out.push(coachingRowFromDb(r));
    if (data.length < PAGE_ROWS) break; // short page = last page
  }

  return out;
}
